/**
 * Equipment validation utilities for detecting mismatches
 * between quote equipment and shipment requirements
 */

/**
 * Determines if two equipment types are compatible
 */
function areEquipmentTypesCompatible(
  quoteEquipment: string,
  shipmentEquipment: string,
): boolean {
  // Define equipment compatibility rules
  const compatibilityMap: Record<string, string[]> = {
    DRY_VAN: ["DRY_VAN", "REEFER"], // Reefer can handle dry cargo
    REEFER: ["REEFER"], // Reefer is specific
    FLATBED: ["FLATBED", "STEP_DECK"], // Step deck can handle some flatbed loads
    STEP_DECK: ["STEP_DECK", "RGN"], // RGN can handle step deck loads
    RGN: ["RGN"], // RGN is specific
    LOWBOY: ["LOWBOY"], // Lowboy is specific
    TANKER: ["TANKER"], // Tanker is specific
    CONTAINER: ["CONTAINER", "CHASSIS"], // Chassis can handle containers
    CHASSIS: ["CHASSIS"], // Chassis is specific
    // Add more compatibility rules as needed
  };

  const compatibleTypes = compatibilityMap[shipmentEquipment] || [
    shipmentEquipment,
  ];
  return compatibleTypes.includes(quoteEquipment);
}

/**
 * Formats equipment type for display
 */
function formatEquipmentType(equipmentType: string): string {
  return equipmentType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Checks if there's an equipment mismatch between the quote and shipment requirements
 */
export function hasEquipmentMismatch(
  quote: ShipperCondensedQuote,
  shipment: ShipperCondensedShipment,
): boolean {
  // If no equipment types specified in shipment, no mismatch
  if (!shipment?.equipments || shipment.equipments.length === 0) {
    return false;
  }

  // If no equipment type in quote, consider it a mismatch
  if (!quote?.equipment_type) {
    return true;
  }

  // Normalize equipment names for comparison (convert to uppercase with underscores)
  const normalizeEquipmentName = (name: string): string =>
    name.toUpperCase().replace(/\s+/g, "_");

  // Check if quote equipment matches any of the shipment's required equipment types
  const quoteEquipmentNormalized = normalizeEquipmentName(quote.equipment_type);
  const shipmentEquipmentTypesNormalized = shipment.equipments.map((eq) =>
    normalizeEquipmentName(eq.type),
  );

  // Direct match with any required equipment type
  if (shipmentEquipmentTypesNormalized.includes(quoteEquipmentNormalized)) {
    return false;
  }

  // Check for compatible equipment types with any required type
  const isCompatibleWithAny = shipmentEquipmentTypesNormalized.some(
    (shipmentEquipmentType) =>
      areEquipmentTypesCompatible(
        quoteEquipmentNormalized,
        shipmentEquipmentType,
      ),
  );

  return !isCompatibleWithAny;
}

/**
 * Gets a human-readable explanation of the equipment mismatch
 */
export function getEquipmentMismatchMessage(
  quote: ShipperCondensedQuote,
  shipment: ShipperCondensedShipment,
): string {
  if (!hasEquipmentMismatch(quote, shipment)) {
    return "";
  }

  const shipmentEquipmentTypes =
    shipment?.equipments?.map((eq) => eq.name) || [];
  const shipmentEquipmentDisplay =
    shipmentEquipmentTypes.length > 0
      ? shipmentEquipmentTypes.join(" or ")
      : "Unknown";
  const quoteEquipment = quote?.equipment_type
    ? formatEquipmentType(quote.equipment_type)
    : "Not specified";

  return `Equipment mismatch: Shipment requires ${shipmentEquipmentDisplay}, but quote offers ${quoteEquipment}`;
}

/**
 * Gets styling properties for equipment mismatch highlighting
 */
export function getEquipmentMismatchStyling(hasMismatch: boolean) {
  if (!hasMismatch) {
    return {};
  }

  return {
    backgroundColor: "rgba(255, 235, 59, 0.2)",
    padding: 0.5,
    borderRadius: 1,
  };
}
